/**
 * React app entry point — mounts at /app/* routes (served as static
 * Vite-built bundle by Express). Coexists with the legacy vanilla pages
 * served from /dashboard, /members, etc. — those keep working unchanged
 * during the migration.
 *
 * Build: `npm run build:app` → outputs to src/public/app/.
 * Dev:   `npm run dev:app` → vite dev server with HMR (separate from
 *        the Express server during dev; production serves the built
 *        bundle from /app/*).
 *
 * Page modules are lazy-loaded so each route ships in its own chunk —
 * keeps the initial dashboard payload small and pushes the heavy
 * d3 / grid-layout deps out of the critical path.
 */
import React, { Suspense, lazy, Component, type ReactNode } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./styles.css";
// Side-effect import: registers all framework-shipped renderers
// (render_chart, render_member_grid, render_funnel, render_diff_panel,
// render_choropleth, etc.) with defaultRegistry. PanelRenderer's
// "renderer" panel kind dispatches via RendererSlot which reads from
// defaultRegistry — so this needs to run BEFORE any route mounts a
// composed panel that references one of those tools. Without it,
// renderer kinds render as nothing on every route.
import "../framework/react/renderers/index.js";
// Side-effect import: registers CongressMCP's per-entity ambient-signal
// renderers with the framework's AmbientSignalRegistry. Today that's
// just "member" → CascadeSignatureBadge; other entity types (contact,
// account, etc.) join here as their substrate signals land.
import "./components/CascadeSignatureBadge.js";
import { Layout } from "./components/Layout";
import { WorkspaceProvider } from "./hooks/useWorkspace";
import { useBrowserBus } from "./hooks/useBrowserBus";
import { usePushNotifications } from "./hooks/usePushNotifications";
import { RouteErrorBoundary } from "./components/ErrorBoundary";
import { committeePortalPath, districtPortalPath } from "./lib/entity-paths";

// ── Root error boundary ─────────────────────────────────────────────────
//
// Sits ABOVE WorkspaceProvider/Routes so any mount-time crash (a stale
// chunk that 404s after a deploy, a context provider blowing up, etc)
// renders a visible error panel instead of a white screen.
//
// Stale-chunk handling: when a deploy ships new content-hashed bundles,
// users who had the old HTML cached request the old chunk URLs and get
// 404s. Vite throws a `ChunkLoadError` (or "Failed to fetch dynamically
// imported module"). We detect that, force-reload ONCE so the browser
// fetches fresh HTML, and only show the error panel if it happens
// twice in a row (indicating a real bug, not a stale cache).
const RELOAD_FLAG = "cmcp:reload-on-chunk-error";

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg = String((err as any)?.message ?? err);
  const name = String((err as any)?.name ?? "");
  return (
    name === "ChunkLoadError" ||
    /Loading (?:CSS )?chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

class RootErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
     
    console.error("[RootErrorBoundary] caught:", error);
    if (isChunkLoadError(error)) {
      // Reload exactly once. The flag clears on successful mount via the
      // useEffect at the top of AppRoot below.
      if (sessionStorage.getItem(RELOAD_FLAG) !== "1") {
        sessionStorage.setItem(RELOAD_FLAG, "1");
        location.reload();
      }
    }
  }

  render() {
    if (this.state.error) {
      const stale = isChunkLoadError(this.state.error);
      return (
        <div style={{ padding: 32, maxWidth: 640, fontFamily: "-apple-system, sans-serif" }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
            {stale ? "We just deployed an update" : "Something went wrong"}
          </h1>
          <p style={{ fontSize: 14, color: "#555", marginBottom: 12 }}>
            {stale
              ? "Your browser had stale assets cached. Reloading should fix it."
              : "The app crashed on load. Reloading sometimes clears it."}
          </p>
          <pre style={{ fontSize: 12, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: 8, color: "#991b1b", overflow: "auto", marginBottom: 12 }}>
            {this.state.error.message}
          </pre>
          <button
            onClick={() => { sessionStorage.removeItem(RELOAD_FLAG); location.reload(); }}
            style={{ padding: "8px 16px", background: "#1a4480", color: "white", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer" }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Clear the chunk-error reload flag after a successful mount.
function ResetReloadFlag() {
  React.useEffect(() => {
    sessionStorage.removeItem(RELOAD_FLAG);
  }, []);
  return null;
}

// Dashboard stays eager — it's the landing route and we don't want the
// suspense fallback flash on first paint.
import { DashboardPage } from "./pages/DashboardPage";

// All other pages: code-split via React.lazy. Each becomes its own
// chunk that's fetched on first navigation and cached thereafter.
const lz = <K extends string, T>(loader: () => Promise<Record<K, T>>, key: K) =>
  lazy(() => loader().then((m) => ({ default: m[key] as React.ComponentType<any> })));

const DesignationsPage = lz(() => import("./pages/DesignationsPage"), "DesignationsPage");
const MembersIndexPage = lz(() => import("./pages/MembersIndexPage"), "MembersIndexPage");
const MemberPortalPage = lz(() => import("./pages/MemberPortalPage"), "MemberPortalPage");
const NetworkAnalyzerPage = lz(() => import("./pages/NetworkAnalyzerPage"), "NetworkAnalyzerPage");
const IssuesIndexPage = lz(() => import("./pages/IssuesIndexPage"), "IssuesIndexPage");
const ResearchIndexPage = lz(() => import("./pages/ResearchIndexPage"), "ResearchIndexPage");
const CaucusesIndexPage = lz(() => import("./pages/CaucusesIndexPage"), "CaucusesIndexPage");
const BillsIndexPage = lz(() => import("./pages/BillsIndexPage"), "BillsIndexPage");
const DiscoverPage = lz(() => import("./pages/DiscoverPage"), "DiscoverPage");
const LegislationDetailPage = lz(() => import("./pages/LegislationDetailPage"), "LegislationDetailPage");
const LegislationMonitorsPage = lz(() => import("./pages/LegislationMonitorsPage"), "LegislationMonitorsPage");
const LegislationMonitorDetailPage = lz(() => import("./pages/LegislationMonitorsPage"), "LegislationMonitorDetailPage");
const DeliveredDigestPage = lz(() => import("./pages/DeliveredDigestPage"), "DeliveredDigestPage");
const NotificationsPage = lz(() => import("./pages/NotificationsPage"), "NotificationsPage");
const DiscussionsPage = lz(() => import("./pages/DiscussionsPage"), "DiscussionsPage");
const PersonalSetupPage = lz(() => import("./pages/PersonalSetupPage"), "PersonalSetupPage");
const LegislatorActivityPage = lz(() => import("./pages/LegislatorActivityPage"), "LegislatorActivityPage");
const RepresentativesPage = lz(() => import("./pages/RepresentativesPage"), "RepresentativesPage");
const BillDetailPage = lz(() => import("./pages/BillDetailPage"), "BillDetailPage");
const AccountsIndexPage = lz(() => import("./pages/AccountsIndexPage"), "AccountsIndexPage");
const AccountPortalPage = lz(() => import("./pages/AccountPortalPage"), "AccountPortalPage");
const ContactsIndexPage = lz(() => import("./pages/ContactsIndexPage"), "ContactsIndexPage");
const ContactPortalPage = lz(() => import("./pages/ContactPortalPage"), "ContactPortalPage");
const CoalitionsIndexPage = lz(() => import("./pages/CoalitionsIndexPage"), "CoalitionsIndexPage");
const CoalitionDetailPage = lz(() => import("./pages/CoalitionDetailPage"), "CoalitionDetailPage");
const CoalitionWikiPage = lz(() => import("./pages/CoalitionWikiPage"), "CoalitionWikiPage");
const WhipCountBoardPage = lz(() => import("./pages/WhipCountBoardPage"), "WhipCountBoardPage");
const WhipBoardsPage = lz(() => import("./pages/WhipBoardsPage"), "WhipBoardsPage");
const ProposalsPage = lz(() => import("./pages/ProposalsPage"), "ProposalsPage");
const CoalitionDashboardPage = lz(() => import("./pages/CoalitionDashboardPage"), "CoalitionDashboardPage");
const MapDashboardPage = lz(() => import("./pages/MapDashboardPage"), "MapDashboardPage");
const OrgBrandingPage = lz(() => import("./pages/OrgBrandingPage"), "OrgBrandingPage");
const ScorecardsIndexPage = lz(() => import("./pages/ScorecardsIndexPage"), "ScorecardsIndexPage");
const ScorecardDetailPage = lz(() => import("./pages/ScorecardDetailPage"), "ScorecardDetailPage");
const ProvisionsPage = lz(() => import("./pages/ProvisionsPage"), "ProvisionsPage");
const ProvisionDetailPage = lz(() => import("./pages/ProvisionDetailPage"), "ProvisionDetailPage");
const ProvisionsConferencePage = lz(() => import("./pages/ProvisionsConferencePage"), "ProvisionsConferencePage");
const SetupPage = lz(() => import("./pages/SetupPage"), "SetupPage");
const SettingsPage = lz(() => import("./pages/SettingsPage"), "SettingsPage");
const IntakePage = lz(() => import("./pages/IntakePage"), "IntakePage");
const IssuePortalPage = lz(() => import("./pages/IssuePortalPage"), "IssuePortalPage");
const TopRelationshipsPage = lz(() => import("./pages/TopRelationshipsPage"), "TopRelationshipsPage");
const ToolHistoryPage = lz(() => import("./pages/ToolHistoryPage"), "ToolHistoryPage");
const LiveOperationsPage = lz(() => import("./pages/LiveOperationsPage"), "LiveOperationsPage");
const FeedbackPage = lz(() => import("./pages/FeedbackPage"), "FeedbackPage");
const ChangelogPage = lz(() => import("./pages/ChangelogPage"), "ChangelogPage");
const AIStudioPage = lz(() => import("./pages/AIStudioPage"), "AIStudioPage");
const LivingReportPage = lz(() => import("./pages/LivingReportPage"), "LivingReportPage");
const LegacyReportRedirect = lz(() => import("./pages/LivingReportPage"), "LegacyReportRedirect");
const PresentationsIndexPage = lz(() => import("./pages/PresentationsIndexPage"), "PresentationsIndexPage");
const PresentationPlayerPage = lz(() => import("./pages/PresentationPlayerPage"), "PresentationPlayerPage");
const TvModePage = lz(() => import("./pages/TvModePage"), "TvModePage");
const PreviewPage = lz(() => import("./pages/PreviewPage"), "PreviewPage");
const PrimitiveShowcase = lz(() => import("./pages/PrimitiveShowcase"), "PrimitiveShowcase");
const CongressPortalPage = lz(() => import("./pages/CongressPortalPage"), "CongressPortalPage");
const StatesIndexPage = lz(() => import("./pages/StatesIndexPage"), "StatesIndexPage");
const StatePortalPage = lz(() => import("./pages/StatePortalPage"), "StatePortalPage");
const StateLegislatorDossierPage = lz(() => import("./pages/StateLegislatorDossierPage"), "StateLegislatorDossierPage");
const CompaniesExplorerPage = lz(() => import("./pages/CompaniesExplorerPage"), "CompaniesExplorerPage");
const TreasuryPipelinesPage = lz(() => import("./pages/TreasuryPipelinesPage"), "TreasuryPipelinesPage");

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default: fetch fresh-ish data, retry once on transient failure.
      // tanstack-query revalidates on window focus + on stale — perfect
      // for our use case where data is mostly read by humans.
      staleTime: 30_000,
      // ...but never retry a plan boundary (402 plan_required).
      retry: (failureCount, error) => !(error instanceof Error && error.name === "PlanRequiredError") && failureCount < 1,
      refetchOnWindowFocus: true,
    },
  },
});

function PageFallback() {
  return <div className="p-8 text-stone-400 text-sm italic">Loading…</div>;
}

function BillDetailRoute() {
  const { billRef } = useParams<{ billRef: string }>();
  const location = useLocation();
  return billRef?.startsWith("state:")
    ? <Navigate to={`/legislation/${encodeURIComponent(billRef)}${location.search}`} replace />
    : <BillDetailPage />;
}

// Entity links follow the URL contract in docs/substrate-interaction-design.md
// (/app/districts/{ST}-{N}, /app/committees/{system_code}). Neither has a
// page of its own; they open the matching Congress portal view.
function DistrictRedirect() {
  const { districtId } = useParams<{ districtId: string }>();
  return <Navigate to={districtPortalPath(districtId)} replace />;
}

function CommitteeRedirect() {
  const { code } = useParams<{ code: string }>();
  return <Navigate to={committeePortalPath(code)} replace />;
}

// The first portal draft lived under /legislatures; Congress and the states
// are now separate sections. Old links redirect, keeping tab and filters.
function LegacyLegislaturesRedirect() {
  const { jurisdiction, tab } = useParams<{ jurisdiction?: string; tab?: string }>();
  const { search } = useLocation();
  if (!jurisdiction) return <Navigate to={`/states${search}`} replace />;
  const base = /^(us|congress|federal)$/i.test(jurisdiction) ? "/congress" : `/states/${jurisdiction.toUpperCase()}`;
  return <Navigate to={`${base}${tab ? `/${tab}` : ""}${search}`} replace />;
}

function AppShell({ children }: { children: React.ReactNode }) {
  // Subscribe to BrowserBus SSE so MCP tool mutations push live updates
  // into the tanstack-query cache without manual refresh.
  useBrowserBus();
  // Register service worker + listen for AI-driven subscribe requests.
  // AI requests and explicit bill-alert controls can prompt for permission.
  usePushNotifications();
  return <Layout>{children}</Layout>;
}

// Routes are split into two groups: shell-less (setup wizard — full-screen,
// no sidebar to escape from) and shelled (everything else — gets the
// Layout sidebar + workspace switcher + live AI session rail).
function ShellRoutes() {
  // Keyed by path so a crash on one page clears when the user navigates away.
  const { pathname } = useLocation();
  return (
    <AppShell>
      <RouteErrorBoundary key={pathname}>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/designations" element={<DesignationsPage />} />
            <Route path="/members" element={<MembersIndexPage />} />
            <Route path="/members/:bioguide" element={<MemberPortalPage />} />
            <Route path="/members/:bioguide/network" element={<NetworkAnalyzerPage />} />
            <Route path="/relationships" element={<TopRelationshipsPage />} />
            <Route path="/tool-history" element={<ToolHistoryPage />} />
            <Route path="/live" element={<LiveOperationsPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/reports" element={<AIStudioPage />} />
            <Route path="/reports/:slug" element={<LivingReportPage />} />
            <Route path="/pages/:slug" element={<LegacyReportRedirect />} />
            <Route path="/ai-canvas/:slug" element={<LegacyReportRedirect />} />
            <Route path="/presentations" element={<PresentationsIndexPage />} />
            <Route path="/issues" element={<IssuesIndexPage />} />
            <Route path="/issues/:issueId" element={<IssuePortalPage />} />
            <Route path="/caucuses" element={<CaucusesIndexPage />} />
            <Route path="/bills" element={<BillsIndexPage />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/legislation/:billRef" element={<LegislationDetailPage />} />
            <Route path="/monitors" element={<LegislationMonitorsPage />} />
            <Route path="/monitors/:monitorId" element={<LegislationMonitorDetailPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/discussions" element={<DiscussionsPage />} />
            <Route path="/notifications/digests/:deliveryId" element={<DeliveredDigestPage />} />
            <Route path="/personal-setup" element={<PersonalSetupPage />} />
            <Route path="/representatives" element={<RepresentativesPage />} />
            <Route path="/legislator-activity" element={<LegislatorActivityPage />} />
            <Route path="/bills/:billRef" element={<BillDetailRoute />} />
            <Route path="/research" element={<ResearchIndexPage />} />
            <Route path="/accounts" element={<AccountsIndexPage />} />
            <Route path="/accounts/:accountId" element={<AccountPortalPage />} />
            <Route path="/companies" element={<CompaniesExplorerPage />} />
            <Route path="/contacts" element={<ContactsIndexPage />} />
            <Route path="/contacts/:contactId" element={<ContactPortalPage />} />
            <Route path="/treasury" element={<TreasuryPipelinesPage />} />
            <Route path="/pipelines" element={<TreasuryPipelinesPage />} />
            <Route path="/coalitions" element={<CoalitionsIndexPage />} />
            <Route path="/coalitions/:coalitionId" element={<CoalitionDetailPage />} />
            <Route path="/coalitions/:coalitionId/wiki/:slug" element={<CoalitionWikiPage />} />
            <Route path="/bills/:billRef/whip-count" element={<WhipCountBoardPage />} />
            <Route path="/issues/:issueId/whip-count" element={<WhipCountBoardPage />} />
            <Route path="/whip-boards" element={<WhipBoardsPage />} />
            <Route path="/whip-boards/:issueId" element={<WhipCountBoardPage />} />
            <Route path="/proposals" element={<ProposalsPage />} />
            <Route path="/coalition-feed" element={<CoalitionDashboardPage />} />
            <Route path="/map" element={<MapDashboardPage />} />
            <Route path="/congress" element={<CongressPortalPage />} />
            <Route path="/congress/:tab" element={<CongressPortalPage />} />
            <Route path="/districts/:districtId" element={<DistrictRedirect />} />
            <Route path="/committees/:code" element={<CommitteeRedirect />} />
            <Route path="/states" element={<StatesIndexPage />} />
            <Route path="/states/:code/legislators/:legislatorId" element={<StateLegislatorDossierPage />} />
            <Route path="/states/:code" element={<StatePortalPage />} />
            <Route path="/states/:code/:tab" element={<StatePortalPage />} />
            <Route path="/legislatures" element={<LegacyLegislaturesRedirect />} />
            <Route path="/legislatures/:jurisdiction" element={<LegacyLegislaturesRedirect />} />
            <Route path="/legislatures/:jurisdiction/:tab" element={<LegacyLegislaturesRedirect />} />
            <Route path="/org-branding/:orgId" element={<OrgBrandingPage />} />
            <Route path="/scorecards" element={<ScorecardsIndexPage />} />
            <Route path="/scorecards/:scorecardId" element={<ScorecardDetailPage />} />
            <Route path="/provisions" element={<ProvisionsPage />} />
            <Route path="/provisions/conference" element={<ProvisionsConferencePage />} />
            <Route path="/provisions/:provisionId" element={<ProvisionDetailPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/intake" element={<IntakePage />} />
            <Route path="/intelligence" element={<Navigate to="/intake" replace />} />
            <Route
              path="*"
              element={
                <div className="p-8 text-stone-500">
                  Page not found.{" "}
                  <Navigate to="/dashboard" replace />
                </div>
              }
            />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </AppShell>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <ResetReloadFlag />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename="/app">
          <WorkspaceProvider>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* Shell-less: onboarding wizard renders full-screen so the
                    sidebar isn't a footgun for users who haven't set up a
                    workspace yet. The presentation player also runs shell-
                    less — it's a fullscreen-takeover surface. */}
                <Route path="/setup" element={<SetupPage />} />
                <Route path="/presentations/:deckId" element={<PresentationPlayerPage />} />
                {/* TV mode: full-screen war-room display of a composed page.
                    Shell-less by design; it subscribes to BrowserBus itself. */}
                <Route path="/tv/:pageKind/:instanceId" element={<TvModePage />} />
                <Route path="/tv/:pageKind" element={<TvModePage />} />
                {/* Preview links from external MCP clients render here without
                    the workspace sidebar — the share-link viewer may not
                    have a workspace context, and we want the panel to be
                    the entire surface. */}
                <Route path="/preview/:token" element={<PreviewPage />} />
                {/* Dev-only primitive showcase. Unlinked, no auth, no
                    workspace — pure visual harness for framework
                    primitives against mock data. See the page's own
                    JSDoc for usage. */}
                <Route path="/dev/primitives" element={<PrimitiveShowcase />} />
                {/* Everything else: standard shelled app. */}
                <Route path="/*" element={<ShellRoutes />} />
              </Routes>
            </Suspense>
          </WorkspaceProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </RootErrorBoundary>
  </React.StrictMode>
);
